import styled from 'styled-components';
import { COLORS } from '../../../constants/style/color';
import { rem } from '../../../utils/style';

export const WhiteLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`;

export const WhiteLabelSection = styled.div`
  background: ${COLORS.White};
  box-shadow: 0px 2px 7px ${COLORS.CardShadow};
  border-radius: 6px;
  padding: ${rem(24)};

  /* Config item - each configuration block within the card */
  .config-item {
    padding-bottom: ${rem(24)};
    margin-bottom: ${rem(24)};
    border-bottom: 1px solid ${COLORS.CulturedWhite};

    &:last-of-type {
      border-bottom: none;
      margin-bottom: ${rem(16)};
      padding-bottom: ${rem(16)};
    }
  }

  .config-item-header {
    margin-bottom: ${rem(16)};

    h4 {
      margin-bottom: ${rem(4)};
    }
  }

  .config-item-content {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  /* Legacy support for section-header/section-content */
  .section-header {
    margin-bottom: ${rem(20)};
    padding-bottom: ${rem(16)};
    border-bottom: 1px solid ${COLORS.CulturedWhite};

    h4 {
      margin-bottom: ${rem(4)};
    }
  }

  .section-content {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .logo-guidelines {
    flex: 1;

    ul {
      margin-top: ${rem(8)};
      padding-left: ${rem(16)};

      li {
        color: ${COLORS.Gray};
        margin-bottom: ${rem(6)};
        list-style-type: disc;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .section-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: ${rem(8)};
    padding-top: 0;
    border-top: none;
  }

  .form-row {
    display: flex;
    flex-direction: column;
    gap: ${rem(16)};

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .form-field {
    flex: 1;
    max-width: ${rem(400)};
  }

  .title-prefix-field {
    margin-top: ${rem(8)};
  }
`;

export const LogoUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  &.favicon-upload {
    .logo-preview {
      width: ${rem(80)};
      height: ${rem(80)};
    }

    .favicon-preview img {
      max-width: ${rem(48)};
      max-height: ${rem(48)};
    }
  }

  .logo-preview {
    width: ${rem(200)};
    height: ${rem(120)};
    border-radius: 6px;
    position: relative;
    border: 2px dashed ${COLORS.CulturedWhite};
    background-color: ${COLORS.OffWhite};
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    transition: border-color 0.3s ease;

    &:hover {
      border-color: ${COLORS.Purple};
    }

    &.is-invalid {
      border-color: ${COLORS.Red};

      &:hover {
        border-color: ${COLORS.Red};
      }
    }

    img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
      padding: ${rem(10)};
    }

    .default-logo {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: ${rem(8)};

      .placeholder-icon {
        font-size: ${rem(40)};
        color: ${COLORS.SpanishGray};
      }

      .placeholder-text {
        color: ${COLORS.SpanishGray};
      }
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    .upload-label {
      display: inline-flex;
      align-items: center;
      background: ${COLORS.White};
      border: 1px solid ${COLORS.CulturedWhite};
      box-shadow: 0px 2px 7px ${COLORS.CardShadow};
      border-radius: 4px;
      padding: ${rem(5)} ${rem(8)};
      position: absolute;
      bottom: ${rem(8)};
      right: ${rem(8)};
      cursor: pointer;
      z-index: 2;
      transition: all 0.3s ease;

      &:hover {
        background: ${COLORS.OffWhite};

        .upload-text {
          color: ${COLORS.Purple};
        }

        .icon {
          color: ${COLORS.Purple};
        }
      }

      &.icon-only {
        padding: ${rem(5)};

        .icon {
          margin-right: 0;
        }
      }

      &.favicon-upload-btn {
        bottom: auto;
        top: ${rem(8)};
        right: ${rem(8)};
      }

      input {
        position: absolute;
        left: -9999px;
      }

      .icon {
        margin-right: ${rem(4)};
        font-size: ${rem(14)};
        color: ${COLORS.Gray};
        transition: color 0.3s ease;
      }

      .upload-text {
        transition: color 0.3s ease;
      }
    }

    .delete-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${COLORS.White};
      border: 1px solid ${COLORS.CulturedWhite};
      box-shadow: 0px 2px 7px ${COLORS.CardShadow};
      border-radius: 4px;
      padding: ${rem(5)};
      position: absolute;
      top: ${rem(8)};
      right: ${rem(8)};
      cursor: pointer;
      z-index: 2;
      transition: all 0.3s ease;

      &:hover {
        background: ${COLORS.LightRed};

        .icon {
          color: ${COLORS.Red};
        }
      }

      .icon {
        font-size: ${rem(16)};
        color: ${COLORS.Gray};
        transition: color 0.3s ease;
      }
    }
  }

  .logo-name {
    margin-top: ${rem(8)};
    color: ${COLORS.Gray};
    max-width: ${rem(200)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
  }
`;

export const LogoErrorMessage = styled.div`
  font-weight: 400;
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: ${COLORS.Red};
  margin-top: ${rem(6)};
  text-align: center;
  max-width: ${rem(200)};
`;
